import React, { useState, useEffect, useCallback } from 'react';
import { marketingService } from '../../_services/marketing.service';
import BeuLoader from '../common/BeuLoader';

const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="80" height="80">
    <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

/**
 * A toggle switch matching the salon-side consent control, so the same
 * affordance means the same thing on both sides of the app.
 */
const Toggle = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    style={{
      width: 44, height: 26, borderRadius: 100, border: 'none', padding: 2, flexShrink: 0,
      background: on ? '#007EA0' : '#DEDEE2',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background 0.15s'
    }}
  >
    <span style={{
      display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s',
      transform: on ? 'translateX(18px)' : 'translateX(0)'
    }} />
  </button>
);

const DashboardMarketingPreferences = ({ history }) => {
  const [salons, setSalons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key currently in flight
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    marketingService.getMyMarketingPreferences()
      .then((res) => {
        const data = res?.data?.responseData || {};
        setSalons(Array.isArray(data.salons) ? data.salons : []);
      })
      .catch(() => setError("We couldn't load your preferences. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (salonId, ownerUserId, channel, subscribed, key) => {
    setSaving(key);
    setError('');
    marketingService.setMyMarketingPreference({ salonId, ownerUserId, channel, subscribed })
      .then(() => load())
      .catch(() => setError("That didn't save. Please try again."))
      .finally(() => setSaving(null));
  };

  return (
    <div className="dashboard-fade-in">
      <div className="dashboard-page-header">
        <button className="dashboard-back-button" onClick={() => history.push('/dashboard/account')}>
          <BackArrowIcon />
        </button>
        <div className="dashboard-page-header-text">
          <h1 className="dashboard-page-title">Marketing preferences</h1>
          <p className="dashboard-page-subtitle">
            Choose who can send you offers and news. Booking confirmations and reminders aren't affected.
          </p>
        </div>
      </div>

      {error && (
        <div className="dashboard-card" style={{ marginBottom: 16, borderLeft: '3px solid #B14438' }}>
          <p style={{ margin: 0, padding: '14px 16px', color: '#B14438', fontSize: 14 }}>{error}</p>
        </div>
      )}

      {loading ? (
        <BeuLoader message="One moment" subtitle="Loading your preferences..." fullScreen={false} />
      ) : !salons || salons.length === 0 ? (
        <div className="dashboard-card" style={{ textAlign: 'center', padding: '48px 24px', color: '#8E8E93' }}>
          <MailIcon />
          <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1A1A1E', margin: '16px 0 6px' }}>Nothing to manage yet</h3>
          <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>
            When a salon or stylist sends you marketing, they'll appear here and you can switch them off any time.
          </p>
        </div>
      ) : (
        salons.map((salon) => (
          <div className="dashboard-card" key={salon.salonId} style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #F2F2F7' }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1A1A1E', margin: 0 }}>
                {salon.salonName || 'Salon'}
              </h2>
              {salon.stoppedAll && (
                <p style={{ fontSize: 13, color: '#A05A1F', margin: '6px 0 0' }}>
                  All marketing from this salon is switched off.
                </p>
              )}
            </div>

            {/* Each brand the client has actually heard from. A salon-wide stop
                overrides the individual switches, so they're disabled and the
                reason is stated rather than leaving dead controls. */}
            {(salon.senders || []).map((sender) => {
              const who = sender.ownerUserId || 'salon';
              return (
                <div key={who} style={{ padding: '16px 20px', borderBottom: '1px solid #F7F7FA' }}>
                  <p style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1E', margin: '0 0 12px' }}>
                    {sender.label}
                  </p>
                  {['email', 'sms'].map((channel) => {
                    const key = `${salon.salonId}:${who}:${channel}`;
                    const on = !!(sender.subscribed && sender.subscribed[channel]);
                    return (
                      <div key={channel} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ fontSize: 14, color: '#4B4B52' }}>
                          {channel === 'email' ? 'Emails' : 'Text messages'}
                        </span>
                        <Toggle
                          on={on}
                          disabled={salon.stoppedAll || saving === key}
                          label={`${channel === 'email' ? 'Emails' : 'Text messages'} from ${sender.label}`}
                          onChange={() => update(salon.salonId, sender.ownerUserId, channel, !on, key)}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <div style={{ padding: '16px 20px' }}>
              {salon.stoppedAll ? (
                <button
                  type="button"
                  disabled={saving === `${salon.salonId}:all`}
                  onClick={() => {
                    // Lifting the salon-wide stop restores the salon's own
                    // switches; individual brands the client turned off stay off.
                    ['email', 'sms'].forEach((ch) =>
                      update(salon.salonId, null, ch, true, `${salon.salonId}:all`));
                  }}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #007EA0',
                    background: '#fff', color: '#007EA0', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  Turn marketing back on
                </button>
              ) : (
                <button
                  type="button"
                  disabled={saving === `${salon.salonId}:all`}
                  onClick={() => {
                    ['email', 'sms'].forEach((ch) =>
                      update(salon.salonId, null, ch, false, `${salon.salonId}:all`));
                  }}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: '1px solid #ECECF0',
                    background: '#fff', color: '#B14438', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit'
                  }}
                >
                  Stop all marketing from {salon.salonName || 'this salon'}
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default DashboardMarketingPreferences;
